"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const fmko_ts_widgets_1 = require("fmko-ts-widgets");
const DefaultGridDataManager_1 = tslib_1.__importDefault(require("./DefaultGridDataManager"));
const GridConstants_1 = tslib_1.__importDefault(require("./GridConstants"));
const SortColumnGroupingLayout_1 = tslib_1.__importDefault(require("./SortColumnGroupingLayout"));
const SimpleGroupingLayout_1 = tslib_1.__importDefault(require("./SimpleGroupingLayout"));
const ImprovedHTMLTable_1 = tslib_1.__importDefault(require("./ImprovedHTMLTable"));
const SortDirection_1 = require("./model/SortDirection");
const TableCssClasses_1 = tslib_1.__importDefault(require("./TableCssClasses"));
const GridColumnFilterUtil_1 = tslib_1.__importDefault(require("./GridColumnFilterUtil"));
class AbstractGrid extends ImprovedHTMLTable_1.default {
    static extractAbstractGridArgs(args) {
        if (!args[0].gridConfig) {
            const [gridConfig] = args;
            return {
                gridConfig,
                gridCssClass: "custom-abstract-grid",
                successMessage: "Data indlæst",
                loadingMessage: "Indlæser data",
                failedMessage: "Det mislykkedes at indlæse data",
                emptyMessage: "Der er ikke registreret noget data"
            };
        }
        return args[0];
    }
    constructor(...args) {
        super();
        this.dataManager = new DefaultGridDataManager_1.default(this);
        this.gridChangedListeners = [];
        this.clickListeners = [];
        const { gridConfig, gridCssClass, tableCssClass = TableCssClasses_1.default.GENERIC, caption, displayCaption = false, summary, loadingMessage, failedMessage, successMessage, emptyMessage, hideIfEmpty = false, testIdentifier } = AbstractGrid.extractAbstractGridArgs(args);
        this.hideIfEmpty = hideIfEmpty;
        this.setCaption(caption || gridConfig.caption);
        this.setSummary(summary || gridConfig.tableSummary);
        this.setGridConfig(gridConfig);
        this.setupClickHandling();
        this.addCssClass(GridConstants_1.default.GRID, gridCssClass);
        this.addTableClasses(GridConstants_1.default.GRID_TABLE, tableCssClass);
        if (!displayCaption) {
            this.addTableClasses(GridConstants_1.default.HIDE_CAPTION);
        }
        if (tableCssClass === TableCssClasses_1.default.GENERIC_FIXED) {
            this.addTableClasses(TableCssClasses_1.default.GENERIC);
        }
        if (testIdentifier) {
            this.getTableElement().dataset.cy = testIdentifier;
        }
        this.loadingWidget = new fmko_ts_widgets_1.LoadingProgressWidget({
            failedMessage,
            loadingMessage,
            successMessage
        });
        this.loadingWidget.addCssClass(GridConstants_1.default.GRID_LOADING);
        this.emptyWidget = new fmko_ts_widgets_1.Div();
        this.emptyWidget.addCssClass(GridConstants_1.default.EMPTY_GRID);
        this.emptyWidget.element.setAttribute("role", fmko_ts_common_1.ARIARole.STATUS);
        this.emptyMessage = emptyMessage;
        this.add(this.loadingWidget);
        this.add(this.emptyWidget);
        this.resetLayout();
        this.render();
    }
    /**
     * Utility method for adding grouping capability to a grid
     */
    addSortColumnGrouping(inlineSingleElementGroups) {
        const groupingGridLayout = new SortColumnGroupingLayout_1.default(this.dataManager, this.gridConfig, this, inlineSingleElementGroups);
        groupingGridLayout.setDelegate(this.getGridLayout());
        this.setGridLayout(groupingGridLayout);
    }
    addStaticGrouping(comparator, presenter, initiallyExpanded = false, enableExpandCollaps = true) {
        const groupingGridLayout = new SimpleGroupingLayout_1.default(this.dataManager, this.gridConfig, this, comparator, presenter, initiallyExpanded, enableExpandCollaps);
        groupingGridLayout.setDelegate(this.getGridLayout());
        this.setGridLayout(groupingGridLayout);
    }
    hasStaticGrouping() {
        const gridLayout = this.getGridLayout();
        return gridLayout instanceof SimpleGroupingLayout_1.default;
    }
    removeStaticGrouping() {
        const gridLayout = this.getGridLayout();
        if (gridLayout instanceof SimpleGroupingLayout_1.default) {
            this.setGridLayout(gridLayout.getDelegate());
            gridLayout.setDelegator(undefined);
        }
    }
    addClickListener(listener) {
        this.clickListeners.push(listener);
    }
    addGridChangedListener(listener) {
        this.gridChangedListeners.push(listener);
    }
    setupGridConfig() {
        this.gridConfig.setGrid(this);
    }
    setupTable() {
        this.setSummary(this.gridConfig.tableSummary);
        this.setCaption(this.gridConfig.caption);
        this.setupHeaders();
        this.setupFooters();
    }
    setupClickHandling() {
        this.element.addEventListener("click", event => {
            let element = this.getElementForEvent(event);
            if (element) {
                this.elementClicked(event, element);
            }
        });
        this.element.addEventListener("keyup", event => {
            let element = this.getElementForEvent(event);
            if (element) {
                this.elementClicked(event, element);
            }
        });
        this.element.addEventListener("keydown", event => {
            let element = this.getElementForEvent(event);
            if (element) {
                this.elementClicked(event, element);
            }
        });
    }
    elementClicked(event, element) {
        // Find the associated table:
        // table -> thead -> tr -> th or
        // table -> tbody -> tr -> td
        const nearestTableAncestor = this.findNearestTableAncestor(element);
        if (nearestTableAncestor === this.getTableElement()) {
            // This is a th/td belonging to the table relevantElement of this grid
            const tableHeaders = this.getTableElement().querySelectorAll(":scope > thead > tr > th");
            const relevantTableHeader = Array
                .from(tableHeaders)
                .find(tableHeader => tableHeader.contains(element));
            if (relevantTableHeader) {
                let column = relevantTableHeader.cellIndex;
                if (event instanceof KeyboardEvent) {
                    if (event.type === "keydown") {
                        if (["Enter", " ", "Spacebar"].includes(event.key)) {
                            event.preventDefault();
                        }
                        return;
                    }
                    if (event.type === "keyup" && !["Enter", " ", "Spacebar"].includes(event.key)) {
                        return;
                    }
                }
                this.headerRowClicked(relevantTableHeader, column);
            }
            else if (element instanceof HTMLTableCellElement && !(event instanceof KeyboardEvent)) {
                let column = element.cellIndex;
                if (this.isRowDataRow(element, column)) {
                    this.dataRowClicked(element, column);
                }
            }
        }
    }
    headerRowClicked(cell, column) {
        if (this.gridConfig.getColumn(column).sortable) {
            if (column !== this.dataManager.getSortedByColumn()) {
                this.dataManager.sort(column, SortDirection_1.SortDirection.DESCENDING);
            }
            else {
                const sortDirection = this.dataManager.getSortDirection() !== SortDirection_1.SortDirection.ASCENDING
                    ? SortDirection_1.SortDirection.ASCENDING
                    : SortDirection_1.SortDirection.DESCENDING;
                this.dataManager.sort(column, sortDirection);
            }
            cell.dispatchEvent(new Event("sort"));
        }
    }
    dataRowClicked(cell, column) {
        if (this.gridConfig.getColumn(column).clickable) {
            let dataForTableRow = this.getDataForTableRow(cell.parentElement);
            for (const data of dataForTableRow) {
                for (const clickListener of this.clickListeners) {
                    clickListener.click(data, column);
                }
            }
        }
    }
    isRowDataRow(cell, _column) {
        const dataRow = cell.parentElement.getAttribute(GridConstants_1.default.GRID_ATTRIBUTE_DATAROW);
        return dataRow && "" !== dataRow;
    }
    getDataForTableRow(row) {
        return this.getGridLayout().tr2DataList(row);
    }
    setupHeaders() {
        this.columnHeaders = [];
        this.columnStyles = [];
        this.filterHeaders = [];
        const numberOfColumns = this.gridConfig.getNumberOfColumns();
        for (let column = 0; column < numberOfColumns; column++) {
            const columnConfig = this.gridConfig.getColumn(column);
            const headerStyleSet = columnConfig.headerStyle instanceof Set
                ? columnConfig.headerStyle
                : columnConfig.headerStyle
                    ? new Set(columnConfig.headerStyle.split(" "))
                    : new Set();
            if (headerStyleSet.has("")) {
                headerStyleSet.delete("");
            }
            if (columnConfig.sortable) {
                headerStyleSet.add(GridConstants_1.default.GRID_COLUMN_SORTABLE);
            }
            this.addHeader(columnConfig.heading, headerStyleSet, GridColumnFilterUtil_1.default.getFilterHeader(columnConfig));
        }
        if (this.dataManager.getSortedByColumn() >= 0 && this.gridConfig.getColumn(this.dataManager.getSortedByColumn()).sortable) {
            this.currentlySortedHeader = {
                column: this.dataManager.getSortedByColumn(),
                sortDirection: this.dataManager.getSortDirection()
            };
        }
    }
    setupFooters() {
        this.columnFooters = [];
        const maximumNumberOfFooters = this.gridConfig.columnConfigs.reduce((currentMax, curr) => Math.max(currentMax, (curr.footers && curr.footers.length) || 0), 0);
        for (const columnConfig of this.gridConfig.columnConfigs) {
            if (columnConfig.footers) {
                for (const footerWrapper of (columnConfig.footers)) {
                    const rowIndex = (columnConfig.footers).indexOf(footerWrapper);
                    let footerClasses = footerWrapper.footerClasses || [];
                    if (columnConfig.sortable) {
                        footerClasses.push(GridConstants_1.default.GRID_COLUMN_SORTABLE);
                    }
                    this.addFooter({
                        footer: footerWrapper.footer,
                        footerClasses: footerClasses,
                        footerStyle: footerWrapper.footerStyle
                    }, rowIndex);
                }
            }
            else {
                Array.from({ length: maximumNumberOfFooters }, ((_, index) => index)).forEach(rowIndex => {
                    this.addFooter({
                        footer: ""
                    }, rowIndex);
                });
            }
        }
    }
    isSortable(columnConfig) {
        return columnConfig.sortable || false;
    }
    getGridConfig() {
        return this.gridConfig;
    }
    setData(data) {
        if (!data) {
            data = [];
        }
        this.dataManager.setData(data);
        this.setLoadingProgressStatus(fmko_ts_common_1.LoadingProgressStatus.SUCCESS);
    }
    dataChanged(sortApplied) {
        if (!sortApplied) {
            this.clearHeadAndBody();
        }
        this.setupHeaders();
        for (const listener of this.gridChangedListeners) {
            listener.gridChanged();
        }
        this.getGridLayout().dataChanged(sortApplied);
        this.setupFooters();
        const isGridEmpty = this.getGridLayout().isEmpty();
        if (this.hideIfEmpty && isGridEmpty) {
            if (this.getTableElement()) {
                (0, fmko_ts_common_1.setElementVisible)(this.getTableElement(), false);
            }
            return;
        }
        if (this.getTableElement()) {
            (0, fmko_ts_common_1.setElementVisible)(this.getTableElement(), true);
        }
        if (sortApplied) {
            this.renderBody();
        }
        else {
            this.render();
        }
    }
    getData() {
        return this.dataManager.getDataRows();
    }
    isEmpty() {
        return !this.getData() || this.getData().length === 0;
    }
    clearData(loadingProgressStatus = fmko_ts_common_1.LoadingProgressStatus.IDLE) {
        this.setLoadingProgressStatus(loadingProgressStatus);
    }
    clearHTMLChildren() {
        super.clearHTMLChildren();
        this.dataManager.clear();
        this.loadingWidget.setStatus(fmko_ts_common_1.LoadingProgressStatus.IDLE);
    }
    getRelatedElements(tr) {
        return tr;
    }
    getSortDirection() {
        return this.dataManager.getSortDirection();
    }
    /**
     *
     * @param {number} column 0-indexed
     * @param {SortDirection} sortDirection
     */
    setSortedByColumn(column, sortDirection) {
        this.dataManager.setSortedByColumn(column, sortDirection);
    }
    /**
     *
     * @returns {number} 0-indexed
     */
    getSortedByColumn() {
        return this.dataManager.getSortedByColumn();
    }
    setHideIfEmpty(hideIfEmpty) {
        this.hideIfEmpty = hideIfEmpty;
    }
    isHideIfEmpty() {
        return this.hideIfEmpty;
    }
    setLoadingProgressStatus(status) {
        this.loadingWidget.setStatus(status);
        this.updateEmptyWidgetVisibility(status);
        const isNotSuccess = status !== fmko_ts_common_1.LoadingProgressStatus.SUCCESS;
        if (isNotSuccess && !this.isEmpty()) {
            this.dataManager.setData([]);
        }
    }
    setGridConfig(gridConfig) {
        this.gridConfig = gridConfig;
        this.setupGridConfig();
        this.setupTable();
    }
    setLoadingMessage(message) {
        this.loadingWidget.setLoadingMessage(message);
    }
    setFailedMessage(message) {
        this.loadingWidget.setFailedMessage(message);
    }
    setSuccessMessage(message) {
        this.loadingWidget.setSuccessMessage(message);
    }
    setEmptyMessage(message) {
        this.emptyMessage = message ?? "Intet data";
    }
    clearHeadAndBody() {
        const table = this.getTableElement();
        const tBodies = table.tBodies;
        for (let i = 0; i < tBodies.length; i++) {
            tBodies[i].remove();
        }
        table.tHead?.remove();
        table.tFoot?.remove();
        // Remove all widgets except loadingWidget and emptyWidget - other widgets belong to thead or tbody
        const children = this.getChildren();
        for (let i = children.length - 1; i >= 0; i--) {
            if (children[i] === this.loadingWidget || children[i] === this.emptyWidget) {
                continue;
            }
            this.remove(i);
        }
    }
    updateEmptyWidgetVisibility(status) {
        const emptyMessage = status === fmko_ts_common_1.LoadingProgressStatus.SUCCESS && this.isEmpty()
            ? this.emptyMessage ?? "Intet data"
            : "";
        this.emptyWidget.setText(emptyMessage);
    }
}
exports.default = AbstractGrid;
//# sourceMappingURL=AbstractGrid.js.map